import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import styles from './ManageGroupStudents.module.sass';
import Card from '../Card';
import StudentSearchBar from '../StudentSearchBar';
import Icon from '../Icon';
import LetterAvatar from '../LetterAvatar';
import { getStudentById } from '../../services/student.service';
import { showSuccess, showError } from '../../services/toast.service';

const ManageGroupStudents = ({ 
    title = "Gestion des participants",
    selectedStudentIds = [], 
    onStudentsChange,
    onSubmit,
    loading = false,
    submitButtonText = "Mettre à jour",
    isFormValid = () => true
}) => {
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [students, setStudents] = useState([]);
    const [loadingStudents, setLoadingStudents] = useState(true);
    const [addingStudent, setAddingStudent] = useState(false);

    // Charger les étudiants depuis les IDs
    useEffect(() => {
        const loadStudents = async () => {
            if (!selectedStudentIds || selectedStudentIds.length === 0) {
                setStudents([]);
                setLoadingStudents(false);
                return;
            }

            setLoadingStudents(true);
            try {
                const studentsPromises = selectedStudentIds.map(id => getStudentById(id));
                const studentsData = await Promise.all(studentsPromises);
                const validStudents = studentsData.filter(s => s && s.id);
                setStudents(validStudents);
            } catch (error) {
                console.error('Erreur lors du chargement des étudiants:', error);
                setStudents([]);
            } finally {
                setLoadingStudents(false);
            }
        };

        loadStudents();
    }, [selectedStudentIds]);

    // Vérifier si l'élève est déjà dans le groupe
    const isStudentAlreadyInGroup = (studentId) => {
        return selectedStudentIds.includes(studentId);
    };

    // Gérer la sélection d'un étudiant
    const handleSelectStudent = (student) => {
        setSelectedStudent(student);
    };

    // Ajouter l'élève au groupe
    const handleAddStudent = async () => {
        if (!selectedStudent) {
            showError('Veuillez sélectionner un élève');
            return;
        }

        if (isStudentAlreadyInGroup(selectedStudent.id)) {
            showError('Cet élève est déjà dans le groupe');
            return;
        }

        setAddingStudent(true);
        try {
            const newStudentIds = [...selectedStudentIds, selectedStudent.id];
            
            // Notifier le parent
            if (onStudentsChange) {
                onStudentsChange({
                    target: { value: newStudentIds }
                });
            }
            
            // Ajouter l'étudiant à la liste locale
            setStudents(prev => [...prev, selectedStudent]);
            
            showSuccess(`${selectedStudent.first_name} ${selectedStudent.last_name} a été ajouté(e)`);
            
            // Réinitialiser la sélection
            setSelectedStudent(null);
        } catch (error) {
            console.error('Erreur lors de l\'ajout:', error);
            showError('Erreur lors de l\'ajout de l\'élève');
        } finally {
            setAddingStudent(false);
        }
    };

    // Supprimer un élève du groupe
    const handleRemoveStudent = (studentToRemove) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir retirer ${studentToRemove.first_name} ${studentToRemove.last_name} ?`)) {
            return;
        }

        const newStudentIds = selectedStudentIds.filter(id => id !== studentToRemove.id);
        
        // Notifier le parent
        if (onStudentsChange) {
            onStudentsChange({
                target: { value: newStudentIds }
            });
        }
        
        // Retirer de la liste locale
        setStudents(prev => prev.filter(s => s.id !== studentToRemove.id));
        
        showSuccess(`${studentToRemove.first_name} ${studentToRemove.last_name} a été retiré(e)`);
    };

    return (
        <Card 
            className={styles.card} 
            title={title}
            classTitle="title-green"
        >
            {/* Section d'ajout d'élève */}
            <div className={styles.addSection}>
                <div className={styles.searchContainer}>
                    <StudentSearchBar
                        className={styles.searchBar}
                        label="Recherchez et ajoutez un élève au groupe."
                        placeholder="Nom, email ou téléphone"
                        onSelectStudent={handleSelectStudent}
                        showDetails={false}
                    />
                </div>

                {selectedStudent && (
                    <div className={styles.selectedStudent}>
                        <div className={styles.studentPreview}>
                            <LetterAvatar
                                firstName={selectedStudent.first_name}
                                lastName={selectedStudent.last_name}
                                size={40}
                            />
                            <div className={styles.studentInfo}>
                                <div className={styles.studentName}>
                                    {selectedStudent.first_name} {selectedStudent.last_name}
                                </div>
                                <div className={styles.studentEmail}>
                                    {selectedStudent.email}
                                </div>
                            </div>
                        </div>
                        
                        <button
                            className={cn("button", styles.addButton)}
                            onClick={handleAddStudent}
                            disabled={addingStudent || isStudentAlreadyInGroup(selectedStudent.id)}
                        >
                            {addingStudent ? (
                                <>
                                    <span className={styles.spinner}></span>
                                    Ajout en cours...
                                </>
                            ) : (
                                <>
                                    <Icon name="plus" size="16" />
                                    Ajouter au groupe
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.divider}></div>

            {/* Liste des élèves du groupe */}
            <div className={styles.participantsSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionTitle}>
                        Élèves du groupe
                    </div>
                    <div className={styles.participantCount}>
                        {students.length} élève{students.length > 1 ? 's' : ''}
                    </div>
                </div>

                {loadingStudents ? (
                    <div className={styles.loading}>Chargement des participants...</div>
                ) : students.length > 0 ? (
                    <div className={styles.participantsList}>
                        {students
                            .sort((a, b) => {
                                const nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
                                const nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
                                return nameA.localeCompare(nameB, 'fr');
                            })
                            .map((student) => (
                            <div key={student.id} className={styles.participantCard}>
                                <div className={styles.participantInfo}>
                                    <LetterAvatar
                                        firstName={student.first_name}
                                        lastName={student.last_name}
                                        size={36}
                                    />
                                    <div className={styles.participantDetails}>
                                        <div className={styles.participantName}>
                                            {student.first_name} {student.last_name}
                                        </div>
                                        <div className={styles.participantEmail}>
                                            {student.email}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.participantActions}>
                                    <button
                                        className={styles.removeButton}
                                        onClick={() => handleRemoveStudent(student)}
                                        title="Retirer du groupe"
                                    >
                                        <Icon name="trash" size="18" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                        <p>Aucun élève dans ce groupe</p>
                        <span>Utilisez la recherche ci-dessus pour ajouter des élèves</span>
                    </div>
                )}
            </div>

            <div className={styles.divider}></div>

            {/* Bouton de sauvegarde */}
            <div className={styles.actions}>
                <button
                    className={cn("button", styles.button)}
                    type="button"
                    onClick={onSubmit}
                    disabled={loading || !isFormValid()}
                >
                    {loading ? "Enregistrement..." : submitButtonText}
                </button>
            </div>
        </Card>
    );
};

export default ManageGroupStudents;
